"use client";

import { Fragment, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { motion, useInView, useReducedMotion, type Transition } from "framer-motion";
import { useRef } from "react";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";

type ComparisonRow = {
  label: string;
  freelance: string;
  agence: string;
  webdifference: string;
};

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    label: "Délai de livraison",
    freelance: "Variable (1-3 mois)",
    agence: "1-2 mois",
    webdifference: "2 semaines",
  },
  {
    label: "Suivi post-lancement",
    freelance: "Rare ou payant",
    agence: "Souvent limité (1 mois)",
    webdifference: "Inclus jusqu'aux premiers résultats",
  },
  {
    label: "SEO + stratégie",
    freelance: "Rarement inclus",
    agence: "Souvent en option",
    webdifference: "Inclus dès le départ",
  },
  {
    label: "Communication",
    freelance: "Dépend de sa charge",
    agence: "Via chef de projet",
    webdifference: "Support 7j/7",
  },
  {
    label: "Garantie de résultat",
    freelance: "Aucune",
    agence: "Rare",
    webdifference: "On ajuste jusqu'à vos premiers résultats",
  },
  {
    label: "Prix",
    freelance: "500€ - 3000€",
    agence: "5000€ - 15000€+",
    webdifference: "7500€",
  },
];

const COLUMNS = [
  { id: "freelance", title: "Freelance", highlighted: false },
  { id: "agence", title: "Agence classique", highlighted: false },
  { id: "webdifference", title: "WebDifference", highlighted: true },
] as const;

const HERO_GRADIENT_BG = "bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600";
const HERO_GRADIENT_TEXT =
  "bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent";

const WD_BORDER = "border-emerald-700";
const WD_SHADOW =
  "0 12px 40px rgba(5, 150, 105, 0.35), 0 4px 16px rgba(13, 148, 136, 0.2)";

const ROW_DIVIDER = "border-t-[0.5px] border-zinc-200/55";
const WD_ROW_DIVIDER = "border-t-[0.5px] border-white/25";

const CELL_PY = "py-3.5 sm:py-4";
const ROW_MIN_HEIGHT = "3.75rem";
const WD_OVERFLOW_CLASS = "-top-12 -bottom-12";
const GRID_COLUMNS = "minmax(10.5rem,1.35fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)";
const HEADER_ROW_CLASS =
  "flex items-center justify-center px-3 text-center text-sm leading-tight sm:text-[0.95rem]";

const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;

const TABLE_DELAY = 0.1;
const HEADER_DELAY = 0.22;
const ROW_BASE_DELAY = 0.3;
const ROW_STAGGER = 0.055;
const CELL_DURATION = 0.48;
const LAST_ROW_DELAY =
  ROW_BASE_DELAY + (COMPARISON_ROWS.length - 1) * ROW_STAGGER + 0.12;
const GREEN_PANEL_DELAY = LAST_ROW_DELAY + CELL_DURATION + 0.08;
const CTA_DELAY = GREEN_PANEL_DELAY;

const GREEN_PANEL_SPRING = {
  type: "spring" as const,
  stiffness: 210,
  damping: 15,
  mass: 0.82,
};

const GREEN_PANEL_WIDTH_SPRING = {
  type: "spring" as const,
  stiffness: 320,
  damping: 24,
  delay: 0.07,
};

function sequenceTransition(
  reducedMotion: boolean | null,
  delay: number,
  duration = CELL_DURATION,
): Transition {
  if (reducedMotion) return { duration: 0 };
  return { delay, duration, ease: SEQUENCE_EASE };
}

function OfferBadge({
  className = "",
  show,
  reducedMotion,
}: {
  className?: string;
  show: boolean;
  reducedMotion: boolean | null;
}) {
  return (
    <motion.span
      className={`inline-flex whitespace-nowrap rounded-full bg-white px-3.5 py-1 text-[0.68rem] font-bold uppercase tracking-widest text-emerald-700 shadow-[0_4px_14px_rgba(0,0,0,0.12)] ${className}`}
      initial={false}
      animate={
        show ? { opacity: 1, scale: 1, y: 0, rotate: 0 } : { opacity: 0, scale: 0.5, y: 10, rotate: -6 }
      }
      transition={
        reducedMotion
          ? { duration: 0 }
          : { delay: 0.22, type: "spring", stiffness: 480, damping: 18 }
      }
    >
      Notre offre
    </motion.span>
  );
}

function ComparisonMobileCard({
  column,
  rows,
  active,
  reducedMotion,
  index,
  showGreenPanel,
}: {
  column: (typeof COLUMNS)[number];
  rows: ComparisonRow[];
  active: boolean;
  reducedMotion: boolean | null;
  index: number;
  showGreenPanel: boolean;
}) {
  const isHighlighted = column.highlighted;
  const delay = 0.12 + index * 0.1;
  const highlightedReady = isHighlighted && showGreenPanel;

  return (
    <motion.article
      className={
        highlightedReady
          ? `relative mt-3 rounded-[20px] border-2 ${WD_BORDER} p-4 pt-8 shadow-[0_12px_40px_rgba(5,150,105,0.35),0_4px_16px_rgba(13,148,136,0.2)] ${HERO_GRADIENT_BG}`
          : "rounded-[18px] bg-white p-4 shadow-[0_12px_36px_-20px_rgba(15,23,42,0.14),0_4px_14px_-10px_rgba(15,23,42,0.06)]"
      }
      initial={false}
      animate={
        active
          ? {
              opacity: 1,
              y: 0,
              scale: highlightedReady && !reducedMotion ? [0.94, 1.03, 1] : 1,
            }
          : { opacity: 0, y: 18, scale: 0.98 }
      }
      transition={
        highlightedReady && !reducedMotion
          ? {
              scale: { duration: 0.62, ease: SEQUENCE_EASE, times: [0, 0.55, 1] },
              y: { type: "spring", stiffness: 280, damping: 20 },
              opacity: { duration: 0.25 },
            }
          : sequenceTransition(reducedMotion, delay, 0.5)
      }
    >
      {isHighlighted ? (
        <OfferBadge
          show={showGreenPanel && active}
          reducedMotion={reducedMotion}
          className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2"
        />
      ) : null}

      <div className="mb-3 text-center">
        <p
          className={
            highlightedReady
              ? "text-base font-bold text-white"
              : "text-base font-semibold text-[#1C1C1C]"
          }
        >
          {column.title}
        </p>
      </div>

      <dl className="flex flex-col">
        {rows.map((row, rowIndex) => {
          const value =
            column.id === "freelance"
              ? row.freelance
              : column.id === "agence"
                ? row.agence
                : row.webdifference;

          return (
            <div
              key={row.label}
              className={`${rowIndex === 0 ? (highlightedReady ? WD_ROW_DIVIDER : ROW_DIVIDER) : highlightedReady ? WD_ROW_DIVIDER : ROW_DIVIDER} ${CELL_PY}`}
            >
              <dt
                className={
                  highlightedReady
                    ? "text-xs font-medium uppercase tracking-[0.06em] text-white/70"
                    : "text-xs font-medium uppercase tracking-[0.06em] text-zinc-400"
                }
              >
                {row.label}
              </dt>
              <dd
                className={
                  highlightedReady
                    ? "mt-1.5 text-sm font-semibold leading-snug text-white"
                    : "mt-1.5 text-sm leading-snug text-zinc-600"
                }
              >
                {value}
              </dd>
            </div>
          );
        })}
      </dl>
    </motion.article>
  );
}

function AnimatedCell({
  active,
  reducedMotion,
  delay,
  className,
  style,
  children,
}: {
  active: boolean;
  reducedMotion: boolean | null;
  delay: number;
  className: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <motion.div
      className={className}
      style={style}
      initial={false}
      animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
      transition={sequenceTransition(reducedMotion, delay)}
    >
      {children}
    </motion.div>
  );
}

export function ComparisonSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });
  const reducedMotion = useReducedMotion();
  const [showGreenPanel, setShowGreenPanel] = useState(false);
  const totalRows = COMPARISON_ROWS.length + 1;
  const gridTemplateRows = `minmax(2.75rem, auto) repeat(${COMPARISON_ROWS.length}, minmax(${ROW_MIN_HEIGHT}, auto))`;
  const gridStyle = {
    gridTemplateColumns: GRID_COLUMNS,
    gridTemplateRows,
  };

  useEffect(() => {
    if (!isInView) return;

    if (reducedMotion) {
      setShowGreenPanel(true);
      return;
    }

    const timer = window.setTimeout(
      () => setShowGreenPanel(true),
      GREEN_PANEL_DELAY * 1000,
    );
    return () => window.clearTimeout(timer);
  }, [isInView, reducedMotion]);

  return (
    <section ref={sectionRef} className="bg-[#f3f3f3] py-10 sm:py-12">
      <div className="mx-auto flex max-w-5xl flex-col gap-4 px-6 sm:gap-5">
        <motion.div
          className="mx-auto w-full max-w-3xl text-center"
          initial={false}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
          transition={sequenceTransition(reducedMotion, 0, 0.55)}
        >
          <h2 className="mx-auto max-w-4xl text-balance text-[1.5rem] font-bold leading-[1.08] tracking-[-0.04em] text-[#1C1C1C] sm:text-3xl md:text-4xl md:leading-[1.05]">
            Nous sommes{" "}
            <span className={HERO_GRADIENT_TEXT}>WebDifférent</span>
          </h2>
        </motion.div>

        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="hidden lg:block">
            <div className="overflow-visible px-1 pb-7 pt-10">
              <motion.div
                className="overflow-visible rounded-[20px] bg-white p-4 shadow-[0_20px_50px_-24px_rgba(15,23,42,0.2),0_8px_24px_-12px_rgba(15,23,42,0.08)] ring-1 ring-zinc-200/60 sm:p-5"
                initial={false}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
                transition={sequenceTransition(reducedMotion, TABLE_DELAY, 0.58)}
              >
                <div className="relative grid" style={gridStyle}>
                  <div
                    className="pointer-events-none relative z-0 col-start-4 row-start-1"
                    style={{ gridRow: `1 / span ${totalRows}` }}
                  >
                    <motion.div
                      className={`absolute inset-x-0 ${WD_OVERFLOW_CLASS} rounded-[18px] bg-emerald-500/35 blur-2xl`}
                      aria-hidden="true"
                      initial={false}
                      animate={
                        showGreenPanel
                          ? { opacity: [0, 0.7, 0.38], scale: [0.82, 1.1, 1] }
                          : { opacity: 0, scale: 0.82 }
                      }
                      transition={
                        reducedMotion
                          ? { duration: 0 }
                          : {
                              duration: 0.85,
                              ease: SEQUENCE_EASE,
                              times: [0, 0.45, 1],
                            }
                      }
                    />

                    <motion.div
                      className={`absolute inset-x-0 ${WD_OVERFLOW_CLASS} overflow-hidden rounded-[18px] border-2 ${WD_BORDER} ${HERO_GRADIENT_BG}`}
                      style={{ boxShadow: WD_SHADOW, transformOrigin: "50% 50%" }}
                      initial={false}
                      animate={
                        showGreenPanel
                          ? {
                              opacity: 1,
                              scaleY: 1,
                              scaleX: 1,
                              filter: "brightness(1) saturate(1)",
                            }
                          : {
                              opacity: 0,
                              scaleY: 0,
                              scaleX: 0.88,
                              filter: "brightness(1.2) saturate(1.15)",
                            }
                      }
                      transition={
                        reducedMotion
                          ? { duration: 0 }
                          : {
                              opacity: { duration: 0.22, ease: "easeOut" },
                              scaleY: GREEN_PANEL_SPRING,
                              scaleX: GREEN_PANEL_WIDTH_SPRING,
                              filter: { duration: 0.55, delay: 0.12, ease: SEQUENCE_EASE },
                            }
                      }
                    >
                      <motion.div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/18 via-transparent to-black/10"
                        initial={false}
                        animate={showGreenPanel ? { opacity: 1 } : { opacity: 0 }}
                        transition={{ delay: 0.18, duration: 0.35, ease: "easeOut" }}
                      />
                      <motion.div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-0 -left-1/2 w-[140%] bg-gradient-to-r from-transparent via-white/35 to-transparent"
                        style={{ skewX: -16 }}
                        initial={false}
                        animate={showGreenPanel ? { x: "118%" } : { x: "-118%" }}
                        transition={
                          reducedMotion
                            ? { duration: 0 }
                            : { delay: 0.32, duration: 0.78, ease: SEQUENCE_EASE }
                        }
                      />
                    </motion.div>

                    <OfferBadge
                      show={showGreenPanel}
                      reducedMotion={reducedMotion}
                      className="absolute left-1/2 -top-12 z-30 -translate-x-1/2 -translate-y-1/2"
                    />
                  </div>

                  <div className="col-start-1 row-start-1" aria-hidden />

                  <AnimatedCell
                    active={isInView}
                    reducedMotion={reducedMotion}
                    delay={HEADER_DELAY}
                    className={`${HEADER_ROW_CLASS} relative z-10 col-start-2 row-start-1 bg-white font-semibold text-[#1C1C1C]`}
                  >
                    Freelance
                  </AnimatedCell>

                  <AnimatedCell
                    active={isInView}
                    reducedMotion={reducedMotion}
                    delay={HEADER_DELAY + 0.05}
                    className={`${HEADER_ROW_CLASS} relative z-10 col-start-3 row-start-1 bg-white font-semibold text-[#1C1C1C]`}
                  >
                    Agence classique
                  </AnimatedCell>

                  <AnimatedCell
                    active={isInView}
                    reducedMotion={reducedMotion}
                    delay={HEADER_DELAY + 0.1}
                    className={`${HEADER_ROW_CLASS} relative z-10 col-start-4 row-start-1 font-bold transition-colors duration-300 ${showGreenPanel ? "text-white" : "text-[#1C1C1C]"}`}
                  >
                    WebDifference
                  </AnimatedCell>

                  {COMPARISON_ROWS.map((row, index) => {
                    const rowIndex = index + 2;
                    const rowDelay = ROW_BASE_DELAY + index * ROW_STAGGER;

                    return (
                      <Fragment key={row.label}>
                        <AnimatedCell
                          active={isInView}
                          reducedMotion={reducedMotion}
                          delay={rowDelay}
                          className={`relative z-10 col-start-1 flex items-center bg-white px-2 ${CELL_PY} text-sm font-semibold text-[#1C1C1C] sm:text-[0.95rem] ${ROW_DIVIDER}`}
                          style={{ gridRowStart: rowIndex }}
                        >
                          {row.label}
                        </AnimatedCell>

                        <AnimatedCell
                          active={isInView}
                          reducedMotion={reducedMotion}
                          delay={rowDelay + 0.04}
                          className={`relative z-10 col-start-2 flex items-center justify-center bg-white px-3 ${CELL_PY} text-center text-sm leading-snug text-zinc-600 ${ROW_DIVIDER}`}
                          style={{ gridRowStart: rowIndex }}
                        >
                          {row.freelance}
                        </AnimatedCell>

                        <AnimatedCell
                          active={isInView}
                          reducedMotion={reducedMotion}
                          delay={rowDelay + 0.08}
                          className={`relative z-10 col-start-3 flex items-center justify-center bg-white px-3 ${CELL_PY} text-center text-sm leading-snug text-zinc-600 ${ROW_DIVIDER}`}
                          style={{ gridRowStart: rowIndex }}
                        >
                          {row.agence}
                        </AnimatedCell>

                        <AnimatedCell
                          active={isInView}
                          reducedMotion={reducedMotion}
                          delay={rowDelay + 0.12}
                          className={`relative z-10 col-start-4 flex items-center justify-center px-3 ${CELL_PY} text-center text-sm font-bold leading-snug transition-colors duration-300 ${showGreenPanel ? `text-white ${WD_ROW_DIVIDER}` : `text-zinc-600 ${ROW_DIVIDER}`}`}
                          style={{ gridRowStart: rowIndex }}
                        >
                          {row.webdifference}
                        </AnimatedCell>
                      </Fragment>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:hidden">
            {COLUMNS.map((column, index) => (
              <ComparisonMobileCard
                key={column.id}
                column={column}
                rows={COMPARISON_ROWS}
                active={isInView}
                reducedMotion={reducedMotion}
                index={index}
                showGreenPanel={showGreenPanel}
              />
            ))}
          </div>

          <motion.div
            className="flex justify-center"
            initial={false}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={sequenceTransition(reducedMotion, CTA_DELAY, 0.5)}
          >
            <HeroCtaButton>Réserver mon audit gratuit</HeroCtaButton>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
